/**
 * Project Selector Component
 *
 * Allows users to select a project for cost tracking context
 * - Only shows if a company is selected (selectedCompanyId in localStorage)
 * - Displays selected project in localStorage as selectedProjectId
 * - Used by AgentMethodsDisplay for cost tracking context
 *
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect } from 'react';
import { Select, Label, Card, Badge } from 'flowbite-react';
import { FolderIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { listProjects, Project } from '../api/projects';

interface ProjectSelectorProps {
  onProjectChange?: (projectId: string) => void;
  className?: string;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onProjectChange,
  className = ''
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Check if company is selected on mount and when localStorage changes
  useEffect(() => {
    const checkCompanySelection = () => {
      const companyId = localStorage.getItem('selectedCompanyId');
      const clientId = localStorage.getItem('selectedClientId');
      setSelectedCompanyId(companyId);
      setSelectedClientId(clientId);

      // If no company selected, clear project selection
      if (!companyId || !clientId) {
        setProjects([]);
        setSelectedProjectId('');
        localStorage.removeItem('selectedProjectId');
      }
    };

    checkCompanySelection();

    // Listen for storage changes (e.g., from other tabs or ClientCompanySelector)
    window.addEventListener('storage', checkCompanySelection);
    return () => window.removeEventListener('storage', checkCompanySelection);
  }, []);

  // Load projects when company is selected
  useEffect(() => {
    const loadProjects = async () => {
      if (!selectedCompanyId || !selectedClientId) {
        setProjects([]);
        setSelectedProjectId('');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Call real API to fetch projects for the company
        const response = await listProjects(selectedClientId, selectedCompanyId, {
          status: 'active' // Only show active projects by default
        });

        if (response.success && response.data.Records) {
          setProjects(response.data.Records);

          // Restore previously selected project if it exists in the new list
          const savedProjectId = localStorage.getItem('selectedProjectId');
          if (savedProjectId && response.data.Records.find(p => p.Project_ID === savedProjectId)) {
            setSelectedProjectId(savedProjectId);
          } else {
            setSelectedProjectId('');
          }
        } else {
          throw new Error(response.message || 'Failed to load projects');
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, [selectedCompanyId, selectedClientId]);

  // Update localStorage when project selection changes
  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('selectedProjectId', selectedProjectId);
      onProjectChange?.(selectedProjectId);
    } else {
      localStorage.removeItem('selectedProjectId');
      onProjectChange?.(selectedProjectId);
    }
  }, [selectedProjectId, onProjectChange]);

  // Don't render if no company selected
  if (!selectedCompanyId) {
    return (
      <Card className={`p-4 bg-amber-50 border border-amber-200 ${className}`}>
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              Select a company first
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Choose a client and company to access project selection
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-4 bg-white border border-gray-200 ${className}`}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <FolderIcon className="h-5 w-5 text-indigo-600" />
          <Label htmlFor="project-select" className="text-sm font-semibold text-gray-900">
            Cost Tracking Context: Project
          </Label>
          {selectedProjectId && (
            <Badge color="blue" size="sm">
              Selected
            </Badge>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-2">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent" />
            <p className="text-sm text-gray-600">Loading projects...</p>
          </div>
        ) : (
          <Select
            id="project-select"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            disabled={isLoading || projects.length === 0}
            sizing="sm"
          >
            <option value="">
              {projects.length === 0 ? 'No projects available' : 'Select a project...'}
            </option>
            {projects.map((project) => (
              <option key={project.Project_ID} value={project.Project_ID}>
                {project.Project_Name} ({project.Project_ID})
              </option>
            ))}
          </Select>
        )}

        {selectedProjectId && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
            <p className="text-xs text-blue-700">
              <strong>Cost tracking context enabled:</strong> Agent executions will be scoped to this project
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProjectSelector;
